import { fn } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { getOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismCheckbox,
  PrismComboSelectInput,
  PrismInput,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import Countries from 'frontend-utils/utils/countries';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import GeneralFormsFormFieldsDatepickerFormField from 'lumen/components/general/forms/form-fields/datepicker-form-field';
import { CHANNEL_PLATFORM } from 'lumen/components/parity/parity-manager/channel-rate-form';
import ParityParityManagerIssueDetailHotelInMarketSearch, {
  type HotelInMarketOption,
} from 'lumen/components/parity/parity-manager/issue-detail-hotel-in-market-search';
import type MarketParityParityManagerAddissueController from 'lumen/controllers/market/parity/parity-manager/addissue';
import type { NewIssue } from 'lumen/controllers/market/parity/parity-manager/addissue';

const BASE_PLATFORM = 'base_platform';

const RATE_TYPE_OPTIONS = [
  { id: 'lowest', name: 'rates.lowest' },
  { id: 'nonrefundable', name: 'rates.nonrefundable' },
  { id: 'bar', name: 'rates.bar_short' },
];

interface LosOption {
  label: string;
  los: number;
}

type PlatformOption =
  MarketParityParityManagerAddissueController['platformOptions'][number];

interface PosOption {
  country: Countries['list'][number];
  label: string;
}

interface ParityParityManagerHotelRateFormSignature {
  Args: {
    cancel: () => void;
    currencyCode: string | undefined;
    isMajorOtaView: boolean;
    issue: NewIssue;
    next: () => void;
    platformOptions: PlatformOption[];
    setIssueProperty: (property: string, newValue: unknown) => void;
    setIssuePropertyFromEvent: (property: string, event: Event) => void;
    toggleValue: (field: string, event: Event) => void;
  };
}

export default class ParityParityManagerHotelRateForm extends Component<ParityParityManagerHotelRateFormSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  // @ts-expect-error: Argument of type 'Owner | undefined' is not assignable to parameter of type 'Owner'.
  countries = new Countries(getOwner(this));

  declare isStartFocused: boolean;
  declare losOptions: LosOption[];
  declare openNoStartDate: boolean;
  declare posOptions: PosOption[];
  declare rateOptions: typeof RATE_TYPE_OPTIONS;

  get isBaseRateValid(): boolean {
    return Boolean(this.issueBaseRate && parseFloat(this.issueBaseRate) > 0);
  }

  get isFormValid(): boolean {
    const { arrival_date: arrivalDate, los, pos } = this.args.issue;

    return Boolean(
      arrivalDate &&
      los &&
      pos &&
      this.issueSubscription?.id &&
      this.isBaseRateValid,
    );
  }

  get issueBaseRate(): string | undefined {
    return this.args.issue?.base_rate;
  }

  get issueSubscription(): NewIssue['subscription'] {
    return this.args.issue?.subscription;
  }

  get losValue(): LosOption | undefined {
    return this.losOptions.find((option) => option.los === this.args.issue.los);
  }

  get minDate(): Dayjs {
    return this.hotelAndMarketService.getLocalTime();
  }

  get posValue(): PosOption | undefined {
    return this.posOptions.find(
      (option) => option.country.id === this.args.issue.pos.id,
    );
  }

  get selectedPlatform(): PlatformOption | undefined {
    return this.args.platformOptions.find(
      (option) => option.id === this.args.issue[BASE_PLATFORM],
    );
  }

  get timezone(): number {
    return this.hotelAndMarketService.timezone;
  }

  constructor(
    owner: Owner,
    args: ParityParityManagerHotelRateFormSignature['Args'],
  ) {
    super(owner, args);
    const losOptions: LosOption[] = [];
    for (let i = 1; i <= 30; i++) {
      losOptions.push({ label: String(i), los: i });
    }

    this.losOptions = losOptions;

    this.posOptions = this.countries.list.map((country) => ({
      label: country.name,
      country,
    }));
    this.rateOptions = RATE_TYPE_OPTIONS;
    this.args.issue.rate = RATE_TYPE_OPTIONS[0];
  }

  @action
  hasSelectedSubscription(selectedOption: HotelInMarketOption): void {
    if (!selectedOption.subscription.id) {
      // It's possible no matches were found. In that case the autocomplete will contain the searchText
      // It's seen as "valid" while we need an actual sub
      // So if you move away from the sub autocomplete, but have not actually chosen a sub, we'll clear it
      this.args.setIssueProperty('subscription', '');
    } else {
      this.args.setIssueProperty('subscription', selectedOption.subscription);
    }
  }

  @action
  onCloseArrivalDate(): void {
    this.isStartFocused = false;
    if (!this.args.issue?.arrival_date) {
      this.openNoStartDate = true;
    } else {
      this.openNoStartDate = false;
    }
  }

  @action
  onOpenArrivalDate(): void {
    this.isStartFocused = true;
  }

  @action
  onSelectionArrivalDate(value: Dayjs | null): void {
    if (value) {
      this.args.setIssueProperty('arrival_date', value.format('YYYY-MM-DD'));
    } else {
      this.args.setIssueProperty('arrival_date', value);
    }
  }

  @action
  setSelectedPlatformId(option: PlatformOption): void {
    this.args.setIssueProperty(BASE_PLATFORM, option.id);
    // Use BASE_PLATFORM as default for CHANNEL_PLATFORM in next step
    this.args.setIssueProperty(CHANNEL_PLATFORM, option.id);
  }

  @action updateBaseRoomName(event: Event): void {
    this.args.setIssueProperty(
      'base_room_name',
      (event.target as HTMLInputElement).value,
    );
  }

  @action updateLos(option: LosOption): void {
    this.args.setIssueProperty('los', option.los);
  }

  @action updatePos(option: PosOption): void {
    this.args.setIssueProperty('pos', option.country);
  }

  <template>
    <div class="flex flex-col p-500" id="add-issue-form">
      <h2 class="header-title pb-400">
        {{t "parity_manager.add_issue.hotel_rate_info"}}
      </h2>
      <div class="flex">
        <div class="flex w-full flex-col pb-400">
          <label class="input-label required-indicator">
            {{t "parity_manager.hotel"}}
          </label>

          <ParityParityManagerIssueDetailHotelInMarketSearch
            @selectItem={{this.hasSelectedSubscription}}
            @selectedOption={{@issue.subscription}}
          />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-400">
        <PrismInput
          @errorMessage={{t "parity_manager.validation.base_rate"}}
          @hasErrors={{not this.isBaseRateValid}}
          @inlineLabel={{@currencyCode}}
          @label={{t "parity.base_rate"}}
          @onInput={{fn @setIssuePropertyFromEvent "base_rate"}}
          @required={{true}}
          @requiredErrorMessage={{t "general.required"}}
          @value={{@issue.base_rate}}
        />

        <div>
          <label class="input-label">{{t "rates.type"}}</label>
          <div class="checkbox flex items-center">
            <PrismCheckbox
              @checked={{@issue.base_cancellation}}
              @onChange={{fn @toggleValue "base_cancellation"}}
            >
              {{t "general.cancellable"}}
            </PrismCheckbox>
          </div>
        </div>
      </div>

      <div class="flex w-full flex-col">
        <PrismInput
          @label={{t "parity_manager.brand_room_name"}}
          @onInput={{this.updateBaseRoomName}}
          @value={{@issue.base_room_name}}
        />
      </div>

      <div class="grid grid-cols-2 gap-400">
        <GeneralFormsFormFieldsDatepickerFormField
          {{! @glint-expect-error: Object literal may only specify known properties, and 'datePickerMinDate' does not exist in type 'NamedArgs<...>'. }}
          @datePickerMinDate={{this.minDate}}
          @label={{t "general.arrival_date"}}
          @onSelectDate={{this.onSelectionArrivalDate}}
          @selectedDate={{@issue.arrival_date}}
        />
        <div class="flex flex-col">
          <label class="input-label required-indicator">
            {{t "rates.los"}}
          </label>
          <PrismSelectInput
            @isSecondary={{true}}
            @onChange={{this.updateLos}}
            @options={{this.losOptions}}
            @unsafe_matchTriggerWidth={{true}}
            @value={{this.losValue}}
          />
        </div>
      </div>

      {{#if @isMajorOtaView}}
        <div class="grid grid-cols-2 gap-400">
          <div class="flex flex-col">
            <label class="input-label required-indicator">
              {{t "general.platform.plural" count=1}}
            </label>
            <PrismComboSelectInput
              @isSecondary={{true}}
              @onChange={{this.setSelectedPlatformId}}
              @options={{@platformOptions}}
              @value={{this.selectedPlatform}}
            />
          </div>
          <div class="flex flex-col">
            <label class="input-label required-indicator">
              {{t "parity.pos.pos"}}
            </label>
            <PrismComboSelectInput
              @isSearchable={{true}}
              @isSecondary={{true}}
              @onChange={{this.updatePos}}
              @options={{this.posOptions}}
              @placeholder={{t "parity.pos.pos"}}
              @value={{this.posValue}}
            />
          </div>
        </div>
      {{/if}}
    </div>

    <div class="settings-modal__footer flex justify-end">
      <GeneralFormFieldsCancelAndConfirm
        @cancel={{@cancel}}
        @confirm={{@next}}
        @confirmText={{t "general.next_step"}}
        @isConfirmDisabled={{not this.isFormValid}}
      />
    </div>
  </template>
}
